import React from 'react';

import { CheckCircle2, HelpCircle } from 'lucide-react';

import { IOS_SWITCH_PROPS } from '../../utils/hapticUtils';

interface PlayedPillProps {
  /** The club's name. Read out in the label, so the control says which course it belongs to. */
  courseName: string;
  isPlayed: boolean;
  onToggle: () => void;
  className?: string;
}

// The one shape for the pill, so Played and the View pill beside it on the compact card stay the
// same size whichever icon or word is in them.
export const playedPillClasses =
  'flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors';

// The member's "I have played this course" switch, in its small form: the shape that fits beside a
// course rather than under it. CourseStateToggles is the full-size pair and is still what the
// detail drawer uses; this is for a row where a labelled checkbox would not fit.
//
// Pulled out of CourseCompactCard when the search results needed the same control. Everything
// below was decided there, and holds wherever the pill goes:
//
// It must read as pressable at a glance, so it is never allowed to look faded — a pale control on
// a dark panel reads as disabled, and this one never is. It is a switch, so it shows its two
// states plainly: a question mark while it is unanswered, a tick once the member says yes.
// The question mark is what makes an untouched pill read as something to press rather than a
// label. It is asking, not stating.
//
// Answered is a solid gold fill with black on it, not a green tick. The client picked this over
// the two green versions it replaced. It means the two states are told apart by fill rather than
// by hue: outlined and quiet while the question stands, filled and loud once it is answered. The
// View pill beside it stays outlined gold, so the filled one is still the only solid thing on the
// card.
//
// Black on the gold, never white: BRAND.md, both gold-on-white and white-on-gold are 2.32:1.
//
// This was emerald first, then a warmer muted green, and both read as borrowed from another
// product on a surface that is otherwise all warm gold.
//
// stopPropagation because every surface that carries this pill is itself pressable, and opens
// something. Without it, ticking played would also bury the map under a drawer. It sits on the
// label so it catches the tap whether it lands on the pill's face or on the input over it.
//
// A label wrapping a real checkbox, not a <button>. It was a button until iOS haptics went in:
// Safari plays its switch haptic only for a finger landing on an actual switch control, and a
// scripted click gets nothing, so the thing the member touches has to be the input. The input is
// stretched over the whole pill at zero opacity and the face underneath is unchanged, which is
// the arrangement that was measured to keep the buzz (see IOS_SWITCH_PROPS). A button cannot
// legally contain a form control, hence the label.
//
// It also describes the control more honestly than the button did: this is a two-state switch,
// so it is a checkbox with role="switch" rather than a button carrying aria-pressed.
const PlayedPill: React.FC<PlayedPillProps> = ({
  courseName,
  isPlayed,
  onToggle,
  className = '',
}) => (
  <label
    onClick={(event) => event.stopPropagation()}
    className={`relative cursor-pointer ${playedPillClasses} ${
      isPlayed
        ? 'border-primary bg-primary text-secondary hover:bg-primary/90'
        : 'border-white/40 bg-white/10 text-white/80 hover:border-primary hover:text-primary'
    } ${className}`}
  >
    <input
      type="checkbox"
      role="switch"
      {...IOS_SWITCH_PROPS}
      checked={isPlayed}
      onChange={onToggle}
      aria-label={
        isPlayed
          ? `Remove ${courseName} from the courses you have played`
          : `Mark ${courseName} as played`
      }
      // Covers the pill exactly, so the finger lands on the switch wherever it hits. m-0 because
      // a checkbox carries a user-agent margin, which would leave a dead strip down one edge.
      className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0"
    />
    {isPlayed ? (
      <CheckCircle2 className="h-3.5 w-3.5" />
    ) : (
      <HelpCircle className="h-3.5 w-3.5" />
    )}
    Played
  </label>
);

export default PlayedPill;
